/** Édition d’un encaissement (date, montant, moyen, référence, notes). */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../components/Button";
import { FxDate } from "../../components/FxDate";
import { FxSelect } from "../../components/FxSelect";
import "../../components/confirmModal.css";
import { useFormatters } from "../../formats/useFormatters";
import { centsToInput, inputToCents, typedDecimal } from "../catalog/types";
import { PAY_KINDS, type Payment } from "./types";

export type PaymentPatch = {
  paid_at: string;
  amount_cents: number;
  method: string;
  reference: string;
  note: string;
};

type PaymentEditModalProps = {
  open: boolean;
  row: Payment | null;
  decimalComma: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (p: PaymentPatch) => void;
};

export function PaymentEditModal({ open, row, decimalComma, busy, onCancel, onConfirm }: PaymentEditModalProps) {
  const { money } = useFormatters();
  const [paidAt, setPaidAt] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("transfer");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open || !row) return;
    setPaidAt(row.paid_at);
    setAmount(centsToInput(row.amount_cents, decimalComma));
    setMethod(row.method || "transfer");
    setReference(row.reference || "");
    setNote(row.note || "");
  }, [open, row, decimalComma]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onCancel]);

  if (!open || !row) return null;
  const cents = inputToCents(amount, decimalComma) ?? 0;
  const room =
    row.invoice_ttc_cents != null && row.document_paid_cents != null
      ? row.invoice_ttc_cents - (row.document_paid_cents - row.applied_cents)
      : null;
  const surplus = room == null ? 0 : cents - room;

  const box = (
    <div className="fx-modal" role="dialog" aria-modal="true" aria-labelledby="fx-pay-edit-title">
      <button type="button" className="fx-modal__backdrop" aria-label="Fermer" disabled={busy} onClick={onCancel} />
      <div className="fx-modal__card">
        <h2 id="fx-pay-edit-title" className="fx-modal__title">
          Éditer le paiement {row.number || ""}
        </h2>
        <div className="fx-pay-fields">
          <label className="fx-pay-fields__lab">
            Date
            <FxDate value={paidAt} onChange={setPaidAt} />
          </label>
          <label className="fx-pay-fields__lab">
            Montant TTC
            <input className="fx-q-ctrl is-end" value={amount} inputMode="decimal" disabled={row.refunded_cents > 0} onChange={(e) => setAmount(typedDecimal(e.target.value, decimalComma))} />
          </label>
          <label className="fx-pay-fields__lab">
            Moyen
            <FxSelect value={method} options={PAY_KINDS} onChange={setMethod} />
          </label>
          <label className="fx-pay-fields__lab">
            Référence
            <input className="fx-q-ctrl" value={reference} onChange={(e) => setReference(e.target.value)} />
          </label>
          <label className="fx-pay-fields__lab">
            Notes privées
            <input className="fx-q-ctrl" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        {surplus > 0 ? (
          <p className="fx-modal__text">
            Surplus de {money(surplus)}. Il reste en trop-perçu sur le client, et s’équilibre au prochain règlement.
          </p>
        ) : null}
        <div className="fx-modal__actions">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Annuler
          </Button>
          <Button
            disabled={busy || cents <= 0}
            onClick={() =>
              onConfirm({
                paid_at: paidAt,
                amount_cents: cents,
                method,
                reference: reference.trim(),
                note: note.trim(),
              })
            }
          >
            {busy ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(box, document.body);
}
